import Image from 'next/image';

import { H3, H5 } from '@common/headline';
import { BodySmall } from '../../common/body';
import { ImageWithExplainerMobile } from '../ImageWithExplainer/ImageWithExplainerMobile';
import { ProgrammeCard } from '../Card/ProgrammeCard';

import recipeBook from '@public/img/medically-supported/recipe-book.png';
import weightTracking from '@public/img/medically-supported/weight-tracking.png';
import handWithPhone from '@public/img/medically-supported/hand-with-phone.png';
import mounjaroProgrammeHealthbox from '@public/img/how-it-works/mounjaro-programme-healthbox.png';

type Props = {
  utmMedium?: string;
  utmCampaign?: string;
};

export const MounjaroAwardWinningProgramme = ({
  utmMedium,
  utmCampaign,
}: Props): JSX.Element => {
  const tiles = [
    {
      image: handWithPhone,
      altText: 'Woman messaging nutritionist on phone',
      title: '1-to-1 support from a nutritionist',
      body: (
        <>
          Get support five days a week from your personal coach, who is a
          registered dietitian or nutritionist. A trusted source of expert
          advice when you need it most.
        </>
      ),
    },
    {
      image: recipeBook,
      altText: 'Second Nature recipe book',
      title: 'Hundreds of simple, indulgent recipes',
      body: (
        <>
          Fall back in love with food again whilst reaching your health goals.
          Say no to boring {"'"}diet{"'"} food, points and calorie-counting.
          Instead, make easy, delicious meals that the whole family love.
        </>
      ),
    },
    {
      image: weightTracking,
      altText: 'Tracking progress in our app',
      title: 'Weight, steps, and sleep tracking',
      body: (
        <>
          Monitor your progress at a glance and keep track of your healthy
          changes in the long-term by having all the data in one place.
        </>
      ),
    },
  ];

  const glp1PriceHeading = 'From £129/mo';
  const glp1PriceSubheading = 'Equivalent to £32/week';

  return (
    <div className="flex w-full flex-col items-center justify-center bg-blue px-3 py-20 lg:px-[60px] lg:py-28">
      <div className="flex text-center text-mono-white">
        <H3>
          Our <span className="text-teal">award-winning</span> programme
        </H3>
      </div>
      <div className="m-auto flex flex-col md:w-[1246px] md:flex-row-reverse md:justify-between">
        <div className="flex max-w-[351px] flex-col items-center justify-start md:w-[50%] md:basis-3/5 lg:w-full lg:max-w-none">
          <div className="mt-[88px] hidden flex-wrap justify-center gap-x-6 gap-y-12 lg:flex">
            {tiles.map(tile => (
              <div className="flex flex-row" key={tile.title}>
                <div className="relative mr-6 h-[138px] w-[148.5px] flex-none overflow-hidden rounded-3xl">
                  <Image
                    alt={tile.altText}
                    layout="fill"
                    objectFit="cover"
                    src={tile.image}
                  />
                </div>
                <div className="mt-8 w-full text-mono-white">
                  <H5>{tile.title}</H5>
                  <div className="mt-2 w-[400px] text-blue-200">
                    <BodySmall>{tile.body}</BodySmall>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-14 flex flex-col gap-12 lg:hidden">
            {tiles.map(tile => (
              <ImageWithExplainerMobile key={tile.title} {...tile} />
            ))}
          </div>
        </div>
        <div className="flex flex-col items-end justify-center pt-20 md:w-[50%] md:basis-2/5">
          <ProgrammeCard
            altText="Mounjaro medication supported programme"
            glp1MedicationIncluded="Yes"
            image={mounjaroProgrammeHealthbox}
            medicationText="Mounjaro medication included"
            priceHeading={glp1PriceHeading}
            priceSubheading={glp1PriceSubheading}
            utmCampaign={utmCampaign}
            utmMedium={utmMedium}
            weightLossPercentage={<span>20-25%</span>}
          />
        </div>
      </div>
    </div>
  );
};
